import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TextDirection } from 'types';

import { dictionaries, Language, languageDirections, StorageKey } from '../consts';
import { StringKey } from '../dictionaries/string-key.enum';

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: StringKey) => string;
  tRich: (key: StringKey, values?: Record<string, string | React.ReactNode>) => React.ReactNode[];
  direction: TextDirection;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setCurrentLanguage] = useState<Language>(Language.ENGLISH);
  const [direction, setDirection] = useState<TextDirection>(languageDirections[Language.ENGLISH]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem(StorageKey.LANGUAGE);
    if (storedLanguage) {
      setCurrentLanguage(storedLanguage as Language);
      setDirection(languageDirections[storedLanguage as Language]);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem(StorageKey.LANGUAGE, lang);
    setCurrentLanguage(lang);
    setDirection(languageDirections[lang]);
  }, []);

  const t = useMemo(
    () => (key: StringKey) => {
      //TODO: Replace hardcoded language after client changes
      return dictionaries[language][key] || key;
    },
    [language],
  );

  const tRich = useMemo(
    () =>
      (key: StringKey, values?: Record<string, string | React.ReactNode>): React.ReactNode[] => {
        //TODO: Replace hardcoded language after client changes
        const template = dictionaries[language][key] || key;

        if (!values) return [template];

        const regex = /{(\w+)}/g;
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        template.replace(regex, (match, token, offset) => {
          if (offset > lastIndex) {
            parts.push(template.slice(lastIndex, offset));
          }

          const replacement = values[token];
          parts.push(replacement !== undefined ? replacement : match);

          lastIndex = offset + match.length;
          return match;
        });

        if (lastIndex < template.length) {
          parts.push(template.slice(lastIndex));
        }

        return parts;
      },
    [language],
  );

  const value = useMemo(
    //TODO: Replace hardcoded values after client changes
    () => ({
      language,
      setLanguage,
      t,
      tRich,
      direction,
    }),
    [language, setLanguage, t, tRich, direction],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
